/**
 * Un puesto del catálogo, el área a la que pertenece y lo que se espera de él.
 *
 * ⚠ **`propias` y `del_flujo` son DOS orígenes distintos y por eso son dos campos.**
 * Las primeras las escribe alguien a mano; las segundas salen de los pasos de los
 * procesos y se mantienen solas. Fundirlos en una sola lista haría imposible saber
 * cuáles se actualizan al cambiar un proceso y cuáles hay que revisar a mano.
 */

/** La altura del puesto en la estructura: decide el acento de su caja en el organigrama. */
export type NivelCargo = "EMPRESA" | "DIVISION" | "DEPARTAMENTO" | "POSICION"

/** De arriba abajo. Son los cuatro de `organizacional` y no se renombran. */
export const NIVELES: readonly { valor: NivelCargo; etiqueta: string }[] = [
  { valor: "EMPRESA", etiqueta: "Empresa" },
  { valor: "DIVISION", etiqueta: "División" },
  { valor: "DEPARTAMENTO", etiqueta: "Departamento" },
  { valor: "POSICION", etiqueta: "Posición" },
]

export function etiquetaDeNivel(nivel: NivelCargo): string {
  return NIVELES.find((n) => n.valor === nivel)?.etiqueta ?? nivel
}

export type Cargo = {
  id: number
  nombre: string
  descripcion: string | null
  funciones: string[]
  departamento_id: number | null
  departamento_nombre: string | null
  nivel: NivelCargo
  activo: boolean
  personas: number
  /** El cargo al que reporta y su nombre, resuelto por el servidor. */
  reporta_a_cargo_id: number | null
  reporta_a_nombre: string | null
  /** Cuántos cargos activos le reportan: se avisa antes de retirarlo. */
  le_reportan: number
  /** (28/10) Los departamentos ADICIONALES: la excepción, no la regla. */
  departamentos_adicionales?: number[]
  /** (28/10) La hoja de ruta del puesto: la inducción de quien llega. */
  hoja_de_ruta?: ActividadRuta[] | null
}

export type ActividadRuta = { dia: number; actividad: string; responsable: string }

export type Departamento = {
  id: number
  nombre: string
  descripcion: string | null
  orden: number
  activo: boolean
  /** Cuántos puestos cuelgan de él, contados en el servidor. */
  cargos: number
  /** El número del área en la estructura («05») y sus siglas («FIN»). */
  codigo: string | null
  siglas: string | null
  /**
   * El CARGO responsable —no la persona: la estructura no cambia al cambiar quien
   * ocupa el puesto— y, resueltos, su nombre y su ocupante (sólo si es UNO).
   */
  cargo_responsable_id: number | null
  cargo_responsable: string | null
  responsable_ocupante: string | null
  responsable_interino: boolean
  /** El área de la que depende. Nulo = de la dirección. */
  padre_id: number | null
  padre: string | null
}

export type Descriptivo = {
  cargo_id: number
  nombre: string
  propias: string[]
  del_flujo: { proceso: string; proceso_id: number; titulo: string; tipo: string }[]
  procesos_a_cargo: string[]
  /** A quién reporta y quiénes le reportan, por nombre de cargo. */
  reporta_a?: string | null
  le_reportan?: string[]
}

/**
 * Lo que se está editando de un cargo, con los campos como los da un formulario.
 *
 * ⚠ **El área va como TEXTO aquí y como número en el cuerpo.** Un `<select>` siempre
 * da texto, y mandarlo tal cual sería un 422 sobre un campo elegido de una lista.
 */
export type Borrador = {
  nombre: string
  descripcion: string
  funciones: string[]
  departamento_id: string
  nivel: NivelCargo
  /** Como texto, por lo mismo que el área. */
  reporta_a_cargo_id: string
  /** Los adicionales, ordenados: así la comparación con lo guardado es directa. */
  departamentos_adicionales: number[]
  hoja_de_ruta: ActividadRuta[]
}

export function borradorDe(c: Cargo): Borrador {
  return {
    nombre: c.nombre,
    descripcion: c.descripcion ?? "",
    funciones: c.funciones,
    departamento_id: c.departamento_id === null ? "" : String(c.departamento_id),
    nivel: c.nivel,
    reporta_a_cargo_id: c.reporta_a_cargo_id === null ? "" : String(c.reporta_a_cargo_id),
    departamentos_adicionales: [...(c.departamentos_adicionales ?? [])].sort((a, b) => a - b),
    hoja_de_ruta: c.hoja_de_ruta ?? [],
  }
}

/**
 * Lo que espera `PATCH /cargos/{id}`.
 *
 * ⚠ **Las funciones van COMPLETAS, no la que se tocó.** Su orden es el que se imprime
 * en el contrato, y una diferencia calculada aquí la aplicarían dos pestañas abiertas
 * sobre el mismo cargo contra un estado que ya cambió.
 */
export function cuerpoDe(b: Borrador) {
  return {
    nombre: b.nombre.trim(),
    descripcion: b.descripcion,
    funciones: b.funciones,
    departamento_id: b.departamento_id === "" ? null : Number(b.departamento_id),
    nivel: b.nivel,
    reporta_a_cargo_id: b.reporta_a_cargo_id === "" ? null : Number(b.reporta_a_cargo_id),
    departamentos_adicionales: b.departamentos_adicionales,
    hoja_de_ruta: b.hoja_de_ruta,
  }
}

/** ¿Hay algo sin guardar? Es lo que enciende el botón de guardar. */
export function estaSucio(b: Borrador, c: Cargo): boolean {
  const guardado = borradorDe(c)
  return (
    b.nombre !== guardado.nombre ||
    b.descripcion !== guardado.descripcion ||
    b.departamento_id !== guardado.departamento_id ||
    b.nivel !== guardado.nivel ||
    b.reporta_a_cargo_id !== guardado.reporta_a_cargo_id ||
    b.departamentos_adicionales.join(",") !== guardado.departamentos_adicionales.join(",") ||
    JSON.stringify(b.hoja_de_ruta) !== JSON.stringify(guardado.hoja_de_ruta) ||
    b.funciones.length !== guardado.funciones.length ||
    b.funciones.some((f, i) => f !== guardado.funciones[i])
  )
}
